import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import parse, { Element } from 'html-react-parser';
import { useForm } from 'react-hook-form';
import { WorryFreeDeliveryRealTimeModalProps } from 'shared/types/cart/Narvar';
import { Money } from 'shared/types/product';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import Loader from '@/components/customercenter-ui/common/loader/loader';
import { getContentByTitle } from '@/components/customercenter-ui/components/product/details/helpers/utils/productDataUtils';
import { AlertDialog, AlertDialogContent } from '@/components/customercenter-ui/ui/alert-dialog';
import { useCheckout } from '@/context/checkoutProvider';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getSelectedDivision } from '@/helpers/utils/siteUtils';
import { AddOrDeclineWFDModalButtons, EmailInputWFDModalButtons } from './worryFreeDeliveryModalButtons';
import { PaymentSchema } from '../schemas';

const WorryFreeDeliveryRealTimeModal = ({
  insuranceItemPrice,
  open,
  setOpen,
  orderHasEmail = false,
  onAdd,
  onDecline,
  loading,
}: WorryFreeDeliveryRealTimeModalProps) => {
  const projectSettings: ProjectSettings = useProjSettings();
  const { contactInformation } = useCheckout();
  const [WFDContent, setWFDContent] = useState('');
  const [loadingContent, setLoadingContent] = useState(true);
  const [email, setEmail] = useState(contactInformation?.email ?? '');
  const [frozenOrderHasEmail, setFrozenOrderHasEmail] = useState(orderHasEmail);
  const WFD_CACHE_KEY = 'WFDModalContent';

  const getWFDContent = (contentData: any) => {
    const withEmailShippingInsuranceContent = mapLocaleToMeaningfulFormat()?.withEmailShippingInsuranceContent;
    const withoutEmailShippingInsuranceContent = mapLocaleToMeaningfulFormat()?.withoutEmailShippingInsuranceContent;
    const key = frozenOrderHasEmail ? withEmailShippingInsuranceContent : withoutEmailShippingInsuranceContent;
    return contentData?.shippingInsurance_ContentEntries?.[0]?.[key]?.content;
  };

  const WFDModalCacheTimeInMinutes = projectSettings?.WFDModalCacheTimeInMinutes ?? 15;

  const getCachedWFDContent = (entryTitle: string) => {
    try {
      const divisionCode = getSelectedDivision();
      const cache = localStorage.getItem(divisionCode + WFD_CACHE_KEY);
      if (!cache) return undefined;
      const parsed = JSON.parse(cache);
      const cachedEntry = parsed?.[entryTitle];
      if (!cachedEntry?.data || !cachedEntry?.date) return undefined;
      const cachedTime = new Date(cachedEntry.date).getTime();
      const currentTime = new Date().getTime();
      const minutesDiff = (currentTime - cachedTime) / (1000 * 60);
      return minutesDiff <= WFDModalCacheTimeInMinutes ? cachedEntry.data : undefined;
    } catch {
      return undefined;
    }
  };

  const setCachedWFDContent = (entryTitle: string, data: any) => {
    try {
      const divisionCode = getSelectedDivision();
      const cache = localStorage.getItem(divisionCode + WFD_CACHE_KEY);
      const parsed = cache ? JSON.parse(cache) : {};
      parsed[entryTitle] = {
        data,
        date: new Date().toISOString(),
      };
      localStorage.setItem(divisionCode + WFD_CACHE_KEY, JSON.stringify(parsed));
    } catch {
      console.error('Error while fetching content.');
    }
  };

  const {
    register,
    trigger,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(PaymentSchema),
    defaultValues: {
      email: email ?? '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (open) {
      setFrozenOrderHasEmail(orderHasEmail);
      const nextEmail = contactInformation?.email ?? '';
      setEmail(nextEmail);
      reset({ email: nextEmail });
    }
  }, [open, reset]);

  useEffect(() => {
    const fetchWorryFreeDeliveryContent = async () => {
      setLoadingContent(true);
      try {
        const entryTitle = projectSettings?.narvarInsuranceContentTitle;
        if (!entryTitle) {
          return;
        }
        let contentData: any = getCachedWFDContent(entryTitle);
        if (!contentData) {
          contentData = await getContentByTitle(entryTitle, true);
          setCachedWFDContent(entryTitle, contentData);
        }
        const WFDModalContent = getWFDContent(contentData);
        const processedWFDContent = WFDModalContent.replace(
          '{{ INSURANCE_PRICE }}',
          typeof insuranceItemPrice === 'string'
            ? insuranceItemPrice
            : CurrencyHelpers.formatForCurrency(
                (insuranceItemPrice as Money) ?? {
                  costInCents: 0,
                  fractionDigits: 2,
                },
              ),
        );
        setWFDContent(processedWFDContent);
      } catch (error) {
        console.error('Error retrieving worry free delivery content:', error);
      } finally {
        setLoadingContent(false);
      }
    };
    fetchWorryFreeDeliveryContent();
  }, [frozenOrderHasEmail, open]);

  const parsedWFDContent = parse(WFDContent, {
    replace: (domNode: any) => {
      if (domNode instanceof Element && domNode.attribs?.id === 'emailModalButtons') {
        return (
          <AddOrDeclineWFDModalButtons
            email={email}
            onAdd={(submittedEmail?: string) => {
              const nextEmail = (getValues('email') || submittedEmail || email || '').trim();
              onAdd(nextEmail);
            }}
            onDecline={onDecline}
            orderHasEmail={frozenOrderHasEmail}
            trigger={trigger}
          />
        );
      }
      if (domNode instanceof Element && domNode.attribs?.id === 'emailInputField') {
        return <EmailInputWFDModalButtons email={email} setEmail={setEmail} register={register} errors={errors} />;
      }
    },
  });

  return (
    <section>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent
          id="worryFreeDelivery-realtime-modal"
          className="custom-scrollbar max-h-[90%] max-w-[400px] overflow-y-auto bg-white p-8 lg:max-w-[652px]"
        >
          {loadingContent ? (
            <div className="flex min-h-[250px] items-center justify-center">
              <Loader />
            </div>
          ) : (
            <>
              {loading && (
                <div className="mb-4 flex items-center justify-center">
                  <Loader />
                </div>
              )}
              {parsedWFDContent}
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default WorryFreeDeliveryRealTimeModal;
